import { CheckCircle } from "lucide-react";

import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { HubLinkListCopy } from "@components/bluetooth-landing/HubLinkList";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";
import {
  contactFormPl,
  mobileClusterLinks,
  mobileKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/mobile-web-landing/shared";

export const title =
  "Tworzenie aplikacji mobilnych i webowych - Android, iOS, SaaS i panele administracyjne";

export const description =
  "Tworzenie aplikacji mobilnych i webowych na zamówienie: Android, iOS, SaaS, panele administracyjne i backend. Polski software house — od wyceny MVP po wdrożenie i utrzymanie.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Tworzenie aplikacji mobilnych i webowych",
    description,
    "Custom mobile and web application development",
  ),
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Usługi aplikacji mobilnych i webowych",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Aplikacje Flutter - jedna aplikacja na Android i iOS",
        url: "https://bluesbrackets.com/aplikacje-flutter-android-ios",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Backend i panele administracyjne",
        url: "https://bluesbrackets.com/backend-i-panele-administracyjne",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "SaaS na zamówienie",
        url: "https://bluesbrackets.com/saas-na-zamowienie",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Integracja AI w aplikacji",
        url: "https://bluesbrackets.com/integracja-ai-w-aplikacji",
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Artykuły o aplikacjach mobilnych i webowych",
    itemListElement: mobileKnowledgeBaseLinks.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.text,
      url: `https://bluesbrackets.com${item.href}`,
    })),
  },
];

export const navbarCopy: NavbarCopy = {
  links: {
    targetAudience: "Dla kogo",
    values: "Baza wiedzy",
    services: "Usługi",
    process: "Proces",
    about: "O nas",
    faq: "FAQ",
  },
  cta: "Wyceń projekt",
};

export const heroCopy: HeroCopy = {
  header: "Aplikacje mobilne",
  headerTail: "i webowe",
  description:
    "Tworzymy aplikacje na zamówienie: Android, iOS, SaaS i panele administracyjne. Polski software house dla firm, które potrzebują produktu cyfrowego z backendem — od pierwszej rozmowy i NDA po MVP i utrzymanie.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Zobacz usługi",
    href: "#uslugi",
  },
  benefits: [
    { text: "Mobile + web + backend", Icon: CheckCircle },
    { text: "MVP, wycena i NDA", Icon: CheckCircle },
    { text: "Flutter, SaaS, AI", Icon: CheckCircle },
  ],
};

export const introCopy = {
  id: "dla-kogo",
  eyebrow: "Dla kogo",
  heading: "Partner software dla aplikacji bez urządzenia",
  paragraphs: [
    "Aplikacja mobilna lub webowa prawie zawsze potrzebuje backendu, panelu administracyjnego albo warstwy SaaS — rzadko działa jako samotny frontend. Dlatego zbieramy te usługi w jednym miejscu: od Fluttera i paneli po SaaS i integracje AI.",
    "Jeśli potrzebujesz aplikacji do urządzenia Bluetooth lub systemu IoT, zajrzyj do naszych stron BLE i IoT. Tu zajmujemy się software'em: Flutter, panele, SaaS i integracje AI — z orientacyjną wyceną, NDA i jasnym zakresem MVP.",
  ],
};

export const servicesListCopy: HubLinkListCopy = {
  id: "uslugi",
  eyebrow: "Usługi",
  heading: "Wybierz obszar najbliższy Twojemu projektowi",
  intro:
    "Każda strona opisuje typowy scenariusz współpracy: Flutter na Android i iOS, backend z panelem, SaaS na zamówienie albo integracja AI w aplikacji.",
  items: [
    {
      href: "/aplikacje-flutter-android-ios",
      title: "Aplikacje Flutter - jedna aplikacja na Android i iOS",
      description:
        "Cross-platform Flutter: MVP, audyt istniejącej aplikacji i utrzymanie — jeden codebase na Android i iOS.",
    },
    {
      href: "/backend-i-panele-administracyjne",
      title:
        "Backend i panele administracyjne - dane, użytkownicy i integracje",
      description:
        "API, dashboard, role, powiadomienia i integracje — backend pod aplikację mobilną lub webową, także przejęcie legacy.",
    },
    {
      href: "/saas-na-zamowienie",
      title: "SaaS na zamówienie - projektowanie i rozwój aplikacji webowych",
      description:
        "Dedykowana platforma SaaS zamiast gotowego produktu, który nie pasuje — subskrypcje, multi-tenant i panel admina.",
    },
    {
      href: "/integracja-ai-w-aplikacji",
      title: "Integracja AI w aplikacji - chatboty, LLM i automatyzacja",
      description:
        "Chatbot AI, OpenAI API i agenci w istniejącej lub nowej aplikacji — z kosztami wdrożenia i RODO na stole.",
    },
  ],
};

export const blogListCopy: HubLinkListCopy = {
  id: "wartosci",
  eyebrow: "Baza wiedzy",
  heading: "Artykuły, które pomagają przed pierwszą rozmową",
  intro:
    "Ile kosztuje aplikacja, jak wygląda proces od UX do wdrożenia, Flutter vs natywnie oraz koszt wdrożenia AI — praktycznie, z perspektywy zamawiającego.",
  items: [
    {
      href: "/pl/blogs/ile-kosztuje-aplikacja-mobilna-lub-webowa",
      title: "Ile kosztuje aplikacja mobilna lub webowa? Przykłady wycen",
      description:
        "Orientacyjne widełki MVP, mobile vs web vs PWA oraz modele fixed price i time & material.",
    },
    {
      href: "/pl/blogs/jak-powstaje-aplikacja-od-ux-po-wdrozenie",
      title:
        "Jak powstaje aplikacja mobilna lub webowa? Od UX do wdrożenia",
      description:
        "Brief, NDA, warsztaty produktowe, MVP i utrzymanie — proces współpracy bez niespodzianek.",
    },
    {
      href: "/pl/blogs/flutter-vs-react-native-vs-natywnie",
      title: "Flutter, React Native czy aplikacja natywna - co wybrać?",
      description:
        "Porównanie stacków pod koszt, utrzymanie i jakość — z uwzględnieniem audytu Flutter.",
    },
    {
      href: "/pl/blogs/ile-kosztuje-wdrozenie-ai-w-aplikacji",
      title: "Ile kosztuje wdrożenie AI w aplikacji? Chatboty i LLM",
      description:
        "Koszt integracji OpenAI, chatbotów i agentów — development, koszty API oraz RODO.",
    },
  ],
};

export const processCopy = {
  id: "proces",
  eyebrow: "Proces",
  heading: "Od pierwszej rozmowy do działającej aplikacji",
  steps: [
    {
      title: "Rozmowa, NDA i wycena",
      description:
        "Omawiamy zakres MVP, podpisujemy NDA jeśli trzeba i dajemy orientacyjną wycenę z modelem współpracy (fixed price lub time & material).",
    },
    {
      title: "MVP i integracje",
      description:
        "Budujemy działający fragment produktu: aplikację, backend, panel i kluczowe integracje (płatności, API, powiadomienia).",
    },
    {
      title: "Wdrożenie i utrzymanie",
      description:
        "Wypuszczamy produkt, ustawiamy monitoring i SLA — albo przejmujemy rozwój istniejącej aplikacji po audycie kodu.",
    },
  ],
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Planujesz aplikację mobilną lub webową?",
    p: "Umów się na darmową konsultację — opowiedz o MVP, SaaS albo panelu admina, a zaproponujemy zakres współpracy i orientacyjną wycenę.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Tworzenie aplikacji mobilnych i webowych na zamówienie. Działamy z Polski, obsługujemy klientów w UE.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Usługi aplikacji" },
      { href: "#wartosci", text: "Baza wiedzy" },
      { href: "#proces", text: "Proces współpracy" },
      ...mobileClusterLinks.filter(
        l => l.href !== "/aplikacje-mobilne-i-webowe",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy aplikacji",
    links: [...mobileKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
